import { useFragment, graphql } from 'react-relay';
import { type FC, type ReactElement, type ComponentProps } from 'react';

// components
import { Link } from 'dibs-elements/exports/Link.tsx';
import { Button } from 'dibs-elements/exports/Button.tsx';
import { SharedContactDealerWrapper } from '../../contactDealer/SharedContactDealerWrapper.tsx';

// types
import { type PdpSharedContactSellerButton_item$key as Item } from './__generated__/PdpSharedContactSellerButton_item.graphql.ts';
import { type PdpSharedContactSellerButton_user$key as User } from './__generated__/PdpSharedContactSellerButton_user.graphql.ts';

const userFragment = graphql`
    fragment PdpSharedContactSellerButton_user on User {
        ...SharedContactDealerWrapper_user
    }
`;

const itemFragment = graphql`
    fragment PdpSharedContactSellerButton_item on Item {
        ...SharedContactDealerWrapper_item
    }
`;

type ButtonProps = ComponentProps<typeof Button>;

export const PdpSharedContactSellerButton: FC<{
    item: Item;
    dataTn?: string;
    currency: string;
    user: User | null | undefined;
    className?: string;
    fullWidth?: boolean;
    launchOrigin?: string;
    itemQuantity?: number;
    selectedSkuId?: string;
    children: ReactElement;
    size?: ButtonProps['size'];
    type?: ButtonProps['type'] | 'link';
    style?: ButtonProps['style'];
    customColors?: ButtonProps['customColors'];
    modalType?:
        | 'REQUEST_PRICE'
        | 'REQUEST_IMAGES'
        | 'CONTACT_DEALER'
        | 'DESIGN_YOUR_OWN'
        | 'REQUEST_CUSTOMIZATION'
        | 'REQUEST_IMAGES_AND_VIDEOS';
    showSellerBranding?: boolean;
}> = ({
    children,
    dataTn,
    type,
    size,
    launchOrigin,
    fullWidth,
    className,
    selectedSkuId,
    itemQuantity,
    currency,
    modalType,
    style,
    customColors,
    showSellerBranding,
    ...props
}) => {
    const user = useFragment(userFragment, props.user);
    const item = useFragment(itemFragment, props.item);

    return (
        <SharedContactDealerWrapper
            item={item}
            user={user}
            currency={currency}
            modalType={modalType}
            itemQuantity={itemQuantity}
            launchOrigin={launchOrigin}
            selectedSkuId={selectedSkuId}
            showSellerBranding={showSellerBranding}
        >
            {(onClick: $TSFixMe) =>
                type === 'link' ? (
                    <Link dataTn={dataTn} onClick={onClick} className={className}>
                        {children}
                    </Link>
                ) : (
                    <Button
                        fullWidth={fullWidth}
                        type={type}
                        size={size}
                        dataTn={dataTn}
                        onClick={onClick}
                        className={className}
                        style={style}
                        customColors={customColors}
                    >
                        {children}
                    </Button>
                )
            }
        </SharedContactDealerWrapper>
    );
};

/**
 * Knip should ignore this duplicated export because it is lazy loaded
 * @alias
 */
export default PdpSharedContactSellerButton;
